/*
  이 모듈은 FastAPI 기반 번역 서비스를 위한 오디오 녹음 및 API 통신 기능을 제공합니다.
  사용자는 마이크를 통해 음성을 입력하고, 해당 음성은 웹 브라우저의 MediaRecorder를 이용해 녹음됩니다.
  녹음된 오디오는 Blob 형태로 서버에 전송되며, 서버는 이를 처리하여 번역된 결과를 반환합니다.
  이 파일은 사용 가능한 언어 목록 제공, 녹음 시작 및 종료, 번역 API 호출, 리소스 정리 등의 기능을 캡슐화하고 있으며,
  Vue 기반 프론트엔드에서 모듈화된 형태로 쉽게 사용할 수 있도록 구성되어 있습니다.
*/


import { apiClientV1 } from '@/api/apiConfig';

// MediaRecorder 관련 상태 변수
let mediaRecorder = null;
let audioChunks = [];
let isRecording = false;
let stream = null; // 스트림을 저장할 전역 변수 추가
// 녹음 시 사용될 MIME 타입을 저장할 변수.
let activeMimeType = '';

// 🎤 음성 입력 감지 관련 변수
let audioContext = null;
let analyser = null;
let microphone = null;
let animationFrameId = null;
let audioLevelCallback = null;  // 음성 레벨을 UI에 전달하는 콜백
let lastAudioLevel = 0;  // 마지막 측정된 음량

// 번역 서비스에서 지원하는 언어 목록 정의
const languages = [
  "Korean", "English", "Japanese", "Chinese", "Spanish",
  "French", "German", "Russian", "Portuguese", "Italian",
  "Vietnamese", "Indonesian", "Thai", "Malay", "Filipino",
  "Dutch", "Polish", "Czech", "Hungarian", "Romanian",
  "Bulgarian", "Croatian", "Albanian", "Macedonian", "Mongolian",
  "Turkish", "Arabic", "Hebrew", "Kurdish", "Persian"
];

// 사용 가능한 언어 목록 반환
export function getAvailableLanguages() {
  return languages;
}

// ============================================================================
// 🎤 음성 레벨 모니터링 함수들
// ============================================================================

/**
 * 🎤 음성 레벨 모니터링 설정
 * @param {MediaStream} audioStream - 마이크 스트림
 */
function setupAudioLevelMonitoring(audioStream) {
  try {
    // AudioContext 생성 (Safari 호환성)
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    audioContext = new AudioContextClass();

    // AnalyserNode 생성
    analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;  // FFT 크기 (작을수록 빠름)
    analyser.smoothingTimeConstant = 0.8;  // 부드러운 전환

    // 마이크 스트림을 AudioContext에 연결
    microphone = audioContext.createMediaStreamSource(audioStream);
    microphone.connect(analyser);

    console.log('🎤 [음성 모니터링] 설정 완료');

    // 실시간 측정 시작
    startAudioLevelMeasurement();
  } catch (error) {
    console.error('🎤 [음성 모니터링] 설정 실패:', error);
  }
}

/**
 * 🎤 실시간 음량 측정 (재귀 호출)
 */
function startAudioLevelMeasurement() {
  if (!analyser) return;

  const bufferLength = analyser.frequencyBinCount;
  const dataArray = new Uint8Array(bufferLength);

  function measure() {
    if (!analyser) return;  // 중단되었으면 종료

    // 주파수 데이터 가져오기
    analyser.getByteFrequencyData(dataArray);

    // 평균 음량 계산 (0-255 → 0-100 정규화)
    let sum = 0;
    for (let i = 0; i < bufferLength; i++) {
      sum += dataArray[i];
    }
    const average = sum / bufferLength;
    const normalizedLevel = Math.round((average / 255) * 100);

    lastAudioLevel = normalizedLevel;

    // UI에 콜백으로 전달
    if (audioLevelCallback) {
      audioLevelCallback(normalizedLevel);
    }

    // 다음 프레임에서 다시 측정 (약 60fps)
    animationFrameId = requestAnimationFrame(measure);
  }

  measure();
}

/**
 * 🎤 음성 모니터링 중지
 */
function stopAudioLevelMonitoring() {
  // 측정 루프 중단
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }

  // AudioContext 정리
  if (microphone) {
    microphone.disconnect();
    microphone = null;
  }

  if (analyser) {
    analyser.disconnect();
    analyser = null;
  }

  if (audioContext && audioContext.state !== 'closed') {
    audioContext.close();
    audioContext = null;
  }

  // 콜백 초기화
  audioLevelCallback = null;
  lastAudioLevel = 0;

  console.log('🎤 [음성 모니터링] 중지 완료');
}

/**
 * 🎤 음성 레벨 콜백 등록
 * @param {Function} callback - (level: number) => void
 */
export function setAudioLevelCallback(callback) {
  audioLevelCallback = callback;
  console.log('🎤 [음성 모니터링] 콜백 등록됨');
}

/**
 * 🎤 마지막 음성 레벨 가져오기
 * @returns {number} 0-100 범위의 음량
 */
export function getLastAudioLevel() {
  return lastAudioLevel;
}

// ============================================================================
// 녹음 시작: 사용자의 마이크에 접근하여 MediaRecorder를 초기화하고 녹음을 시작함
// ============================================================================
export async function startTranslation() {
  if (isRecording) return false; // 중복 녹음 방지

  // HTTPS 또는 localhost 환경이 아닐 경우, 또는 mediaDevices를 지원하지 않을 경우 에러 처리
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    console.error('[오류] 마이크 접근 불가: 이 기능은 보안(HTTPS) 연결 또는 localhost에서만 작동합니다.');
    alert('마이크를 사용하려면 보안(HTTPS) 연결이 필요합니다.');
    return false;
  }

  try {
    // 해결책: 브라우저가 지원하는 오디오 포맷을 확인하여 가장 호환성 높은 것을 선택합니다.
    const supportedTypes = ['audio/mp4', 'audio/webm'];
    activeMimeType = supportedTypes.find(type => MediaRecorder.isTypeSupported(type));

    if (!activeMimeType) {
      throw new Error("브라우저에서 지원하는 오디오 녹음 형식이 없습니다.");
    }
    console.log(`[녹음] 사용될 오디오 포맷: ${activeMimeType}`);

    // 스트림이 없거나 비활성화 상태일 때만 새로 요청
    if (!stream || !stream.active) {
      console.log('[스트림] 새로운 마이크 스트림을 요청합니다.');
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    }
    
    // 결정된 MIME 타입으로 MediaRecorder를 초기화합니다.
    mediaRecorder = new MediaRecorder(stream, { mimeType: activeMimeType });
    audioChunks = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) audioChunks.push(event.data);
    };

    console.log("번역 시작 버튼 눌렀을 때 2 isRecording", isRecording)

    mediaRecorder.start(1000); // 1초 단위 데이터 수집
    isRecording = true;

    // ✨ 음성 레벨 모니터링 시작
    setupAudioLevelMonitoring(stream);

    console.log('[녹음] 번역을 위한 음성 녹음 시작 + 음성 모니터링 활성화');
    return true;
  } catch (error) {
    console.error('[오류] 마이크 접근 또는 녹음 초기화 실패:', error);
    throw error;
  }
}

// 녹음 종료 및 번역 요청 전송: 녹음을 마치고 서버로 전송하여 번역 결과를 받음
export function stopTranslation(options) {
  if (!isRecording || !mediaRecorder || mediaRecorder.state === 'inactive') {
    console.warn('[녹음] 중지 요청이 들어왔으나, 녹음 중이 아니므로 무시합니다.');
    return;
  }

  // 1. 녹음 중지 시 onstop 이벤트 핸들러 설정
  mediaRecorder.onstop = () => {
    // 2. Blob 데이터 생성
    const audioBlob = new Blob(audioChunks, { type: activeMimeType });
    audioChunks = []; // 다음 녹음을 위해 청크 비우기

    if (audioBlob.size > 0) {
      // 3. 파일 업로드를 백그라운드에서 실행 (결과를 기다리지 않음)
      console.log('[업로드] 백그라운드에서 오디오 파일 업로드를 시작합니다.');
      sendAudioForTranslation(audioBlob, options)
        .catch(error => {
          // 백그라운드에서 발생한 에러는 콘솔에 기록만 함 (UI 블로킹 방지)
          console.error('[오류] 백그라운드 오디오 업로드 실패:', error);
          // 필요하다면 여기에 사용자에게 알리는 비간섭적인 토스트 메시지 등을 추가할 수 있음
        });
    } else {
      console.warn('[녹음] 녹음된 데이터가 없어 업로드를 건너뜁니다.');
    }
  };

  // 4. 녹음 상태를 즉시 false로 변경하고 녹음기를 중지
  isRecording = false;
  mediaRecorder.stop();
  console.log('[녹음] 녹음 중지. UI는 즉시 반응해야 합니다.');
}

/**
 * 진행 중인 녹음을 데이터 전송 없이 강제로 중지하고 리소스를 정리합니다.
 * 서버로부터 발언권이 해제되었을 때 등 클라이언트의 상태를 강제로 초기화할 때 사용됩니다.
 */
export function cancelTranslation() {
  if (mediaRecorder && mediaRecorder.state === 'recording') {
    // 스트림은 닫지 않고 녹음기만 중지
    mediaRecorder.onstop = null; // onstop 이벤트 핸들러 제거
    mediaRecorder.stop();
    console.log('[녹음] 녹음이 강제 취소되었습니다.');
  }
  isRecording = false;
  audioChunks = [];
  // mediaRecorder = null; // cleanup에서 처리
}

/**
 * 🚫 음성이 감지되지 않아 녹음 취소
 * ⚠️ 중요: 서버로 오디오를 전송하지 않음
 * 3초 동안 음성이 감지되지 않았을 때 호출됨
 */
export function cancelSilentRecording() {
  console.log('🚫 [녹음 취소] 음성이 감지되지 않아 녹음을 취소합니다.');

  if (!isRecording || !mediaRecorder || mediaRecorder.state === 'inactive') {
    console.warn('🚫 [녹음 취소] 녹음 중이 아니므로 무시합니다.');
    return;
  }

  // 1. MediaRecorder 이벤트 핸들러 제거 (서버 전송 방지)
  mediaRecorder.onstop = null;
  mediaRecorder.ondataavailable = null;

  // 2. 녹음 중지
  if (mediaRecorder.state !== 'inactive') {
    mediaRecorder.stop();
  }

  // 3. 오디오 청크 버리기 (서버 전송 안 함)
  audioChunks = [];

  // 4. 상태 초기화
  isRecording = false;

  // 5. 음성 모니터링 중지
  stopAudioLevelMonitoring();

  console.log('🚫 [녹음 취소] 완료 - 서버로 데이터 전송하지 않음');
}

// 서버에 오디오 Blob과 옵션을 전송하여 번역 요청
async function sendAudioForTranslation(audioBlob, options) {
  try {
    // ⏱️ E2E 지연 시간 측정 시작
    const requestStartTime = performance.now();
    window.lastTranslationRequestStartTime = requestStartTime;

    console.log('[요청] 번역 요청 전송 시작');
    console.log('[요청] 사용자 이름:', options.userName);
    console.log('[요청] 회의 ID:', options.code);
    console.log('[요청] 사용자 역할:', options.isHost ? '개설자' : '참가자');

    // MIME 타입에 따라 파일 확장자를 동적으로 결정합니다.
    const fileExtension = activeMimeType.includes('mp4') ? 'mp4' : 'webm';
    const filename = `recording.${fileExtension}`;
    console.log(`[녹음] 전송될 파일명: ${filename}`);

    const formData = new FormData();
    formData.append('audio_file', audioBlob, filename);
    formData.append('source_language', options.sourceLanguage);
    formData.append('translation_option', options.translationOption);
    // if (options.code) formData.append('code', options.code); // 기존 코드
    if (options.code) formData.append('meeting_id', options.code); // 수정된 코드: 'code' -> 'meeting_id'

    // 사용자 정보 추가 2025.06.02 안예진: 스크립트 내 발화자 구분을 위한 코드 추가
    formData.append('user_id', options.userId || '');
    formData.append('user_name', options.userName || '사용자');
    // 개설자 여부 추가: 개설자인 경우 "개설자", 참가자인 경우 "참가자"
    const userRole = options.isHost ? '개설자' : '참가자';
    formData.append('user_role', userRole);

    // 디버깅: FormData 내용 로그 출력
    console.log('[요청] FormData 전송 필드:');
    console.log('  - audio_file:', filename);
    console.log('  - source_language:', options.sourceLanguage);
    console.log('  - translation_option:', options.translationOption);
    console.log('  - meeting_id:', options.code);
    console.log('  - user_id:', options.userId || '');
    console.log('  - user_name:', options.userName || '사용자');
    console.log('  - user_role:', userRole, `(isHost: ${options.isHost})`);

    const response = await apiClientV1.post('/api/v1/audio/input/transcribe', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    // ⏱️ API 응답 시간 로깅
    const apiResponseTime = performance.now();
    const apiLatency = apiResponseTime - requestStartTime;
    console.log('⏱️ [성공] 번역 요청 전송 완료');
    console.log(`⏱️ [API 지연시간] REST API 응답까지: ${apiLatency.toFixed(2)}ms (${(apiLatency / 1000).toFixed(2)}초)`);
    console.log('[API 응답] transcribe API response:', response.data);
    return { message: '전송 완료', response: response.data };
  } catch (error) {
    console.error('[오류] 서버에 번역 요청 실패:', error);
    throw error;
  }
}

// 현재 녹음 중인지 여부 반환
export function isCurrentlyRecording() {
  return isRecording;
}

// 녹음 중지 및 모든 관련 리소스 해제
export function cleanup() {
  if (isRecording && mediaRecorder && mediaRecorder.state !== 'inactive') {
    mediaRecorder.stop();
  }
  if (stream) {
    stream.getTracks().forEach(track => track.stop());
    console.log('[스트림] 마이크 스트림이 정리되었습니다.');
  }

  // ✨ 음성 모니터링 정리 추가
  stopAudioLevelMonitoring();

  isRecording = false;
  mediaRecorder = null;
  audioChunks = [];
  stream = null;

  console.log('[정리] 모든 녹음 리소스 해제 완료');
}